import { useCallback, useMemo, useState } from "react";
import { useCaptureVisitorLead } from "@/hooks/leads/useLeads";
import { trackEvent, trackEventOnce } from "@/lib/ga4";
import { reportLeadCaptured } from "@/lib/leadConversions";
import {
  getCapturedSummaryEmail,
  markSummaryCaptured,
  markSummaryDismissed,
  shouldHideSummaryCapture,
} from "@/lib/summaryCapture";
import type {
  SummaryCaptureValues,
  UseSummaryCaptureOptions,
} from "@/types/voice/summary-capture";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Business logic for the "Get a summary" capture flow. Owns the progressive
 * form state, validation, and lead persistence so both the persistent
 * action-row chip and (later) the Genie-summoned panel can reuse it.
 *
 * The save goes through the API's public `visitor-leads` action. It used to be
 * a direct `leads` insert, which the table's security rules refuse for anyone
 * not signed in to Help Genie, so real visitors always saw "Something went
 * wrong". The server resolves the genie's owner, so `agentUserId` is no longer
 * sent.
 */
export const useSummaryCapture = ({
  agentId,
  source = "summary_chip",
  conversationId,
  onCaptured,
}: UseSummaryCaptureOptions) => {
  const createLead = useCaptureVisitorLead();

  const [values, setValues] = useState<SummaryCaptureValues>({
    email: "",
    name: "",
    phone: "",
  });
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True once the visitor has reopened an already-captured form to correct
  // the email — shows the editable form instead of the quiet "Recap goes to…"
  // notice until they submit again or navigate away.
  const [editing, setEditing] = useState(false);

  const alreadyCaptured = useMemo(() => shouldHideSummaryCapture(), []);
  const capturedEmail = useMemo(() => getCapturedSummaryEmail(), []);

  const onChange = useCallback((patch: Partial<SummaryCaptureValues>) => {
    setError(null);
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  const reset = useCallback(() => {
    setValues({ email: "", name: "", phone: "" });
    setExpanded(false);
    setSubmitted(false);
    setError(null);
    setEditing(false);
  }, []);

  const dismiss = useCallback(() => {
    markSummaryDismissed();
    trackEvent("live_agent_capture_dismissed", { agent_id: agentId, source });
  }, [agentId, source]);

  // Reopens the ask, prefilled with the email already on file, so a "Change"
  // tap never makes the visitor retype what they already gave us.
  const startEdit = useCallback(() => {
    setValues((prev) => ({ ...prev, email: capturedEmail ?? prev.email }));
    setSubmitted(false);
    setEditing(true);
  }, [capturedEmail]);

  // Fires once per source per browser session (GA4 sessionizes on its own, and
  // most surfaces here don't carry a conversationId) — call when the ask/quiet
  // notice actually becomes visible to the visitor, not on hook mount.
  const markShown = useCallback(() => {
    if (!agentId) return;
    trackEventOnce(`capture_shown:${source}:${agentId}`, "live_agent_capture_shown", {
      agent_id: agentId,
      source,
    });
  }, [agentId, source]);

  const submit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      const email = values.email.trim();
      const name = values.name?.trim();
      const phone = values.phone?.trim();

      if (createLead.isPending) return;
      if (!EMAIL_RE.test(email)) {
        setError("Please enter a valid email address.");
        return;
      }

      try {
        if (agentId) {
          await createLead.mutateAsync({
            email,
            ...(name ? { name } : {}),
            ...(phone ? { phone } : {}),
            agent_id: agentId,
            source,
            ...(conversationId ? { conversation_id: conversationId } : {}),
            page_url: window.location.href,
          });
        }

        markSummaryCaptured(email);
        setSubmitted(true);
        setEditing(false);
        trackEvent("live_agent_capture_submitted", { agent_id: agentId, source });
        if (agentId) reportLeadCaptured({ agentId, conversationId, source });
        onCaptured?.(email);
      } catch (err) {
        console.error("Summary capture failed:", err);
        setError("Something went wrong. Please try again.");
      }
    },
    [values, createLead, agentId, source, conversationId, onCaptured],
  );

  return {
    values,
    onChange,
    expanded,
    toggleExpanded,
    submit,
    dismiss,
    reset,
    isSubmitting: createLead.isPending,
    submitted,
    error,
    alreadyCaptured,
    capturedEmail,
    editing,
    startEdit,
    markShown,
  };
};
